import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    getLoopSlippage,
    LoopPositionExpanded,
    useUnwindQuoteAndIxs,
    BsMetaUtil,
    getZcCollateral,
    getZcLedger,
    getDefaultWindType,
    calculateLoopFairMarketValue,
    useLoopscaleWalletContext,
    useRepayLoanTransaction,
    useTokenListContext,
    useUnwindStats,
    AbfLoanExpanded,
    LoanCollateralExpanded,
    UnwindTokenStats,
    useSwapCollateralTransaction,
    getAssetIdentifier,
    getAssetType,
    getMintFromAssetData,
    useBuildLoanTransactions,
    BuildLoanTransactionsRequest,
    LoanCollateral,
    RepayLoanTxnParams,
    SwapCollateralTxnParams,
    TokenListMetadata,
    SimulatedOrderedTransactions
} from "@bridgesplit/abf-react";
import { SwapInstructionsResponse, WindType } from "@loopscale/pandora-types";
import {
    BalanceChanges,
    formatUsd,
    uiAmountToLamports,
    Result,
    createAnalyticsInfo,
    AnalyticsTransactionType,
    LoopAnalyticsInfo,
    WRAPPED_SOL_MINT
} from "@bridgesplit/utils";
import {
    Column,
    Row,
    Text,
    MEDIA,
    OptionSelectList,
    useDialog,
    TagTextAlert,
    TextExternalLink,
    Span
} from "@bridgesplit/ui";
import { COPY, DOCS } from "app/constants";
import { useMediaQuery, Box, CircularProgress, useTheme } from "@mui/material";
import { TransactionTypes } from "app/types";
import { AppDialog } from "app/utils";
import {
    buildSwapCollateralParamsFromStep,
    getSwapInstructionSteps,
    mergeRouteInstructionSteps,
    RouteInstructionStep
} from "app/utils/swapInstructions";

import {
    AppButton,
    Slippage,
    useLoopUnwindAlerts,
    LoopDisplayType,
    PandoraQuoteError,
    formatSlippageRowEndText,
    TokenImage,
    useLoanOraclePriceQuotes
} from "../../common";
import { createRepaymentParams } from "./payments/util";
import { MakeRepaymentParams } from "./payments/types";
import { useCloseLoop } from "./useCloseLoop";
import { useExecutionDetails } from "../../loop/execution-detail";
import { ExecutionDetailsLabels } from "../../loop/execution-detail/types";
import { ActionsSettingsButton } from "./common";
import {
    UnwindOption,
    OPTION_WIND_TYPE,
    UseRepayLoopPreviewBalancesReturn,
    UnwindOptionData,
    UseUnwindOptionsParams,
    BaseUnwindQuoteParams,
    UseJitoUnwindSimulationReturn
} from "./types";
import { LoanPositionStats } from "./LoanPositionStats";

export default function LoopUnwind({
    loopPosition: loopPositionProp,
    inNestedDialogView
}: {
    loopPosition: LoopPositionExpanded | undefined;
    inNestedDialogView: boolean;
}) {
    const loopPosition = useMemo(() => loopPositionProp, [loopPositionProp?.loanExpanded.loan.address]);
    const { slippagePresetsUi, defaultSlippageUi } = getLoopSlippage(loopPosition?.loopExpanded);
    const {
        slippagePercentDecimals,
        slippagePercentUi,
        slippageWrapperProps,
        slippageButtonProps,
        slippageDialogProps
    } = Slippage.useController({
        defaultSlippageUi,
        presetsUi: slippagePresetsUi
    });
    const { selectedOption, setSelectedOption } = useDisplayController(loopPosition);

    const { open } = useDialog();

    const loopName = loopPosition?.loopExpanded?.loopVault.name;
    const loanExpanded = loopPosition?.loanExpanded;
    const loanCollateral = getZcCollateral(loopPosition?.loanExpanded);
    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const debtToRepay = ledger?.ledgerDebt.total;
    const principalMetadata = loanExpanded?.principalMetadata;

    // Loop actions always broadcast via Jito; previously gated on loopVault.metadata?.jito?.close.
    const jitoCloseEnabled = true;

    const quoteParams = {
        loopPositionExpanded: loopPosition,
        slippagePercentDecimals: slippagePercentDecimals ?? 0,
        debtToRepay,
        loanCollateral,
        collateralAmount: loanCollateral?.amount,
        loanExpanded
    };

    const { getMetadata: getRouteMetadata } = useTokenListContext();

    const resolveMetadataForMint = useCallback(
        (mint: string | undefined) => {
            if (!mint) return undefined;
            if (loanCollateral?.assetMint === mint) return loanCollateral.metadata;
            if (principalMetadata?.mint === mint) return principalMetadata;
            return getRouteMetadata(mint);
        },
        [getRouteMetadata, loanCollateral, principalMetadata]
    );

    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const swapCollateralTx = useSwapCollateralTransaction();
    // always use jito for unwind if loan builder is being used
    const loanBuilder = useBuildLoanTransactions(true);
    const { options, loading, availableOptions } = useUnwindOptions({
        quoteParams,
        jitoCloseEnabled,
        loanExpanded,
        loanCollateral,
        debtToRepay,
        resolveMetadataForMint,
        activeWallet,
        loopPosition,
        loopName,
        getTransactionsWithParamsAndSimulation: loanBuilder.getTransactionsWithParamsAndSimulation
    });

    const optionValues = Object.values(options ?? {});
    const hasOptions = optionValues.length > 0;
    const allQuoteFailed = hasOptions && optionValues.every((option) => !!option?.quoteResult.error);
    const allSimulationFailed = hasOptions && optionValues.every((option) => !!option?.simulationFailed);

    const selectedQuote = options?.[selectedOption];
    const selectedQuoteError = !!selectedQuote?.quoteResult.error;
    const externalQuoteError = selectedQuote?.quoteResult.error;

    // No routes resolved at all: either the loop vault failed to expand (so `loopPosition` is
    // undefined) or every unwind route is disabled on the vault. Both leave `allowedWindTypes`
    // empty, and the quote/simulation error paths below can't fire without options — so without
    // this the borrower gets an empty card and a permanently dead CTA with no explanation.
    const noRoutesAvailable = !loading && !hasOptions;

    const disabled =
        !selectedQuote?.tokenStats ||
        selectedQuoteError ||
        !availableOptions?.[selectedOption] ||
        !!selectedQuote?.isFetchingQuote ||
        !!selectedQuote?.simulationFailed ||
        loading;

    const { closeLoop } = useCloseLoop({
        selectedQuote,
        loanExpanded,
        jitoCloseEnabled,
        selectedOption,
        loanBuilder,
        swapCollateralTx
    });

    const { unwindWarningMessage } = useLoopUnwindAlerts({
        loopPositionExpanded: loopPosition,
        variant: "route"
    });

    return (
        <Slippage.Wrapper {...slippageWrapperProps}>
            {inNestedDialogView && <Text variant="h4">Close loop</Text>}

            <Column spacing={0.5}>
                <ActionsSettingsButton
                    label="Close to"
                    rowEndText={formatSlippageRowEndText(slippagePercentUi, slippagePercentDecimals)}
                    onClick={() => {
                        if (inNestedDialogView) {
                            return slippageButtonProps.open();
                        }
                        return open(AppDialog.SlippageSettings, slippageDialogProps);
                    }}
                />

                {!allQuoteFailed && (
                    <ReceiveOptionsList
                        options={options}
                        selectedOption={selectedOption}
                        setSelectedOption={setSelectedOption}
                        loading={loading}
                        resolveMetadataForMint={resolveMetadataForMint}
                        loopPosition={loopPosition}
                    />
                )}
            </Column>

            {!allQuoteFailed && (
                <SecondaryStats
                    inNestedDialogView={inNestedDialogView}
                    loading={loading}
                    error={selectedQuoteError}
                    loopPosition={loopPosition}
                    tokenStats={selectedQuote?.tokenStats}
                    windType={selectedQuote?.windType}
                    slippagePercentDecimals={slippagePercentDecimals}
                    externalQuote={selectedQuote?.quoteResult.data}
                />
            )}

            {unwindWarningMessage ? (
                <Text variant="body2" color="caption">
                    {unwindWarningMessage}
                </Text>
            ) : null}
            {noRoutesAvailable && <UnwindRoutesUnavailable />}
            {(allQuoteFailed || allSimulationFailed) && (
                <PandoraQuoteError externalQuoteError={externalQuoteError} allSimulationFailed={allSimulationFailed} />
            )}
            <AppButton
                disabled={disabled}
                isTransaction
                transactionType={TransactionTypes.LoopUnwind}
                asyncCta={{ onClickWithResult: closeLoop }}
            >
                {COPY.UNWIND_TERM}
            </AppButton>
        </Slippage.Wrapper>
    );
}

function UnwindRoutesUnavailable() {
    return (
        <TagTextAlert color="error" icon="warning">
            <Span>
                Closing isn&apos;t available for this loop right now. Please check back later or{" "}
                <TextExternalLink
                    href={DOCS.manualLoopClose}
                    sx={{ color: "secondary.main", display: "inline" }}
                    isPrimary
                    underlineLink
                >
                    manually manage your position.
                </TextExternalLink>
            </Span>
        </TagTextAlert>
    );
}

function useUnwindOptionData({
    option,
    allowed,
    quoteParams,
    jitoCloseEnabled,
    loanExpanded,
    loanCollateral,
    debtToRepay,
    resolveMetadataForMint,
    activeWallet,
    loopPosition,
    loopName,
    getTransactionsWithParamsAndSimulation
}: {
    option: UnwindOption;
    allowed: boolean;
    quoteParams: BaseUnwindQuoteParams;
    jitoCloseEnabled: boolean;
    loanExpanded: AbfLoanExpanded | undefined;
    loanCollateral: LoanCollateralExpanded | undefined;
    debtToRepay: number | undefined;
    resolveMetadataForMint: (mint: string | undefined) => TokenListMetadata | undefined;
    activeWallet: string | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    loopName: string | undefined;
    getTransactionsWithParamsAndSimulation: (
        params: BuildLoanTransactionsRequest
    ) => Promise<Result<SimulatedOrderedTransactions>>;
}): UnwindOptionData {
    const windType = OPTION_WIND_TYPE[option];
    // Loop actions always broadcast via Jito; the flag only gates the simulation strategy
    // (simulateBundle needs multi-tx, which we ensure via splitAtaCreates + unifySetup: false).
    const useJitoSimulation = allowed;
    const quoteResult = useUnwindQuoteAndIxs({
        ...quoteParams,
        windType,
        skip: !allowed,
        outputToken: loanExpanded?.principalMetadata
    });
    const swapInstructionSteps = useMemo(() => getSwapInstructionSteps(quoteResult.data), [quoteResult.data]);
    const swapStep = swapInstructionSteps.length > 1 ? swapInstructionSteps[0] : undefined;
    const repayInstructionStep: RouteInstructionStep | undefined = useMemo(() => {
        if (swapInstructionSteps.length > 1) {
            return mergeRouteInstructionSteps(swapInstructionSteps.slice(1)) ?? swapInstructionSteps[1];
        }
        return swapInstructionSteps[0];
    }, [swapInstructionSteps]);
    const swapCollateralParams = useMemo(() => {
        if (!swapStep || !loanExpanded) return undefined;
        return buildSwapCollateralParamsFromStep({
            loanAddress: loanExpanded.loan.address,
            collateralAmount: uiAmountToLamports(loanCollateral?.amount, loanCollateral?.metadata.decimals || 0),
            step: swapStep,
            resolveMetadataForMint
        });
    }, [loanCollateral, loanExpanded, resolveMetadataForMint, swapStep]);
    const repayParams = useMemo(
        () =>
            createRepaymentParams({
                principalPaymentUiAmount: debtToRepay,
                collateralPaymentUiAmount: loanCollateral?.amount,
                isFullRepay: true,
                repayType: windType,
                collateral: loanCollateral,
                loanExpanded,
                quote: quoteResult.data,
                isLoopUnwind: true,
                instructionStep: repayInstructionStep
            }),
        [debtToRepay, loanCollateral, loanExpanded, quoteResult.data, repayInstructionStep, windType]
    );

    const repayTx = useRepayLoanTransaction(true);

    const withdrawCollateral = useMemo(
        () =>
            buildSimulatedCollateralForRepay({
                loanExpanded,
                swapCollateralParams,
                resolveMetadataForMint,
                jitoCloseEnabled
            }),
        [jitoCloseEnabled, loanExpanded, resolveMetadataForMint, swapCollateralParams]
    );

    const repayTxnParams = useLoopRepayTxnParams({
        loanExpanded,
        loanCollateral,
        activeWallet,
        loopName,
        debtToRepay,
        repayParams,
        externalQuote: quoteResult.data,
        withdrawCollateral
    });

    const {
        previewBalances,
        isLoading: simulationLoading,
        error: simulationError,
        hasSimulated: simulationHasSimulated
    } = useRepayLoopPreviewBalances({
        loanExpanded,
        loanCollateral,
        debtToRepay,
        repayTxnParams,
        externalQuote: quoteResult.data,
        windType,
        repayTx,
        useJitoSimulation,
        swapInstructionSteps,
        swapCollateralParams,
        getTransactionsWithParamsAndSimulation
    });

    const tokenStats = useUnwindStats({
        loopPosition,
        externalQuote: quoteResult.data,
        previewBalances
    });
    const simulationFailed = simulationHasSimulated && simulationError;
    const hasQuoteError = quoteResult.isError;

    const initialLoadCompleteRef = useRef(false);
    const lastStableRef = useRef<{
        tokenStats: UnwindTokenStats | undefined;
        hasQuoteError: boolean;
        simulationFailed: boolean;
    } | null>(null);

    const readyFromError = hasQuoteError || simulationFailed;
    const readyFromSuccess = !quoteResult.isLoading && !simulationLoading && simulationHasSimulated && !simulationError;

    if (!initialLoadCompleteRef.current && (readyFromError || readyFromSuccess)) {
        initialLoadCompleteRef.current = true;
    }

    if (readyFromError || readyFromSuccess) {
        lastStableRef.current = { tokenStats, hasQuoteError, simulationFailed };
    }

    const initialLoadComplete = initialLoadCompleteRef.current;
    const isFetchingQuote = initialLoadComplete && (quoteResult.isFetching || simulationLoading);

    const useSnapshot = initialLoadComplete && isFetchingQuote && lastStableRef.current !== null;
    const effectiveTokenStats = useSnapshot ? (lastStableRef.current?.tokenStats ?? tokenStats) : tokenStats;
    const effectiveHasQuoteError = useSnapshot
        ? (lastStableRef.current?.hasQuoteError ?? hasQuoteError)
        : hasQuoteError;
    const effectiveSimulationFailed = useSnapshot
        ? (lastStableRef.current?.simulationFailed ?? simulationFailed)
        : simulationFailed;
    return {
        allowed,
        windType,
        quoteResult,
        repayTxnParams,
        repayTx,
        swapInstructionSteps,
        swapCollateralParams,
        simulation: {
            previewBalances,
            isLoading: simulationLoading,
            error: simulationError,
            hasSimulated: simulationHasSimulated
        },
        tokenStats: effectiveTokenStats,
        simulationFailed: effectiveSimulationFailed,
        useJitoSimulation,
        hasQuoteError: effectiveHasQuoteError,
        initialLoadComplete,
        isFetchingQuote
    };
}

function useUnwindOptions(params: UseUnwindOptionsParams): {
    options: Record<UnwindOption, UnwindOptionData> | undefined;
    availableOptions: Record<UnwindOption, boolean> | undefined;
    loading: boolean;
    fetching: boolean;
} {
    const {
        quoteParams,
        jitoCloseEnabled,
        loanExpanded,
        loanCollateral,
        debtToRepay,
        resolveMetadataForMint,
        activeWallet,
        loopPosition,
        loopName,
        getTransactionsWithParamsAndSimulation
    } = params;

    const allowedWindTypes = loopPosition?.allowedWindTypes ?? [];
    const collateralAllowed = allowedWindTypes.includes(WindType.Collateral);
    const principalAllowed = allowedWindTypes.includes(WindType.Principal);

    const collateralData = useUnwindOptionData({
        option: "collateral",
        allowed: collateralAllowed,
        quoteParams,
        jitoCloseEnabled,
        loanExpanded,
        loanCollateral,
        debtToRepay,
        resolveMetadataForMint,
        activeWallet,
        loopPosition,
        loopName,
        getTransactionsWithParamsAndSimulation
    });

    const principalData = useUnwindOptionData({
        option: "principal",
        allowed: principalAllowed,
        quoteParams,
        jitoCloseEnabled,
        loanExpanded,
        loanCollateral,
        debtToRepay,
        resolveMetadataForMint,
        activeWallet,
        loopPosition,
        loopName,
        getTransactionsWithParamsAndSimulation
    });

    const { options, availableOptions, loading, fetching } = useMemo(() => {
        const expected: UnwindOption[] = [];
        if (collateralAllowed) expected.push("collateral");
        if (principalAllowed) expected.push("principal");

        const dataMap = { collateral: collateralData, principal: principalData };

        if (expected.length === 0) {
            return { options: undefined, availableOptions: undefined, loading: false, fetching: false };
        }

        // Initial load is complete for an option once it has produced a usable state
        // (either a successful quote+simulation or a terminal error). We keep this
        // sticky so polling/refetching doesn't flip `loading` back to true.
        const allInitialLoadsComplete = expected.every((o) => dataMap[o].initialLoadComplete);
        const loading = !allInitialLoadsComplete;

        const options = Object.fromEntries(expected.map((o) => [o, dataMap[o]])) as Record<
            UnwindOption,
            UnwindOptionData
        >;
        const availableOptions = Object.fromEntries(expected.map((o) => [o, dataMap[o].allowed])) as Record<
            UnwindOption,
            boolean
        >;

        const fetching = expected.some((o) => dataMap[o].isFetchingQuote);

        return { options, availableOptions, loading, fetching };
    }, [collateralAllowed, principalAllowed, collateralData, principalData]);

    return {
        options: options ?? undefined,
        availableOptions: availableOptions ?? undefined,
        loading,
        fetching
    };
}

export function useSimulatedBalanceChanges({
    skip,
    repayTxnParams,
    repayTx
}: {
    skip: boolean;
    repayTxnParams: RepayLoanTxnParams | undefined;
    repayTx: ReturnType<typeof useRepayLoanTransaction>;
}): { data: BalanceChanges | undefined; isLoading: boolean; error: boolean; hasSimulated: boolean } {
    const [data, setData] = useState<BalanceChanges | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasSimulated, setHasSimulated] = useState(false);

    useEffect(() => {
        setData(undefined);
        setIsLoading(false);
        setError(false);
        setHasSimulated(false);

        if (skip || !repayTxnParams) return;
        const params = repayTxnParams;

        let cancelled = false;

        async function simulate() {
            setIsLoading(true);
            try {
                const result = await repayTx.getTransactionsWithParamsAndBalanceChanges(params); //error here
                if (cancelled) return;

                if (result.isErr()) {
                    setData(undefined);
                    setError(true);
                    return;
                }

                const balanceChanges = result.unwrap().balanceChanges;
                if (!balanceChanges) {
                    setData(undefined);
                    setError(true);
                    return;
                }

                setData(balanceChanges);
                setError(false);
            } catch (error) {
                if (cancelled) return;
                setData(undefined);
                setError(true);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                    setHasSimulated(true);
                }
            }
        }

        void simulate();

        return () => {
            cancelled = true;
        };
        // repayTx goes crazy
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skip, repayTxnParams]);

    return { data, isLoading, error, hasSimulated };
}

function useLoopRepayTxnParams({
    loanExpanded,
    loanCollateral,
    activeWallet,
    debtToRepay,
    repayParams,
    externalQuote,
    withdrawCollateral,
    loopName,
    slippagePercentDecimals
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loanCollateral: LoanCollateralExpanded | undefined;
    activeWallet: string | undefined;
    debtToRepay: number | undefined;
    repayParams: MakeRepaymentParams;
    externalQuote: SwapInstructionsResponse | undefined;
    withdrawCollateral: LoanCollateral | undefined;
    loopName?: string;
    slippagePercentDecimals?: number;
}): RepayLoanTxnParams | undefined {
    const loanAddress = loanExpanded?.loan.address;
    const principalMetadata = loanExpanded?.principalMetadata;

    return useMemo(() => {
        if (
            !loanAddress ||
            !principalMetadata ||
            !loanCollateral ||
            !activeWallet ||
            debtToRepay === undefined ||
            !repayParams.mintToReceive ||
            !externalQuote
        )
            return undefined;

        return {
            loan: loanAddress,
            repayParams: [
                {
                    amount: uiAmountToLamports(debtToRepay, principalMetadata.decimals),
                    ledgerIndex: 0,
                    repayAll: true
                }
            ],
            collateralWithdrawalParams: [
                withdrawCollateral
                    ? {
                          collateralMint: withdrawCollateral.assetMint,
                          amount: withdrawCollateral.amount,
                          closeIfEligible: true
                      }
                    : {
                          collateralMint: loanCollateral.assetMint,
                          amount: uiAmountToLamports(loanCollateral.amount, loanCollateral.metadata.decimals),
                          closeIfEligible: true
                      }
            ],
            cpiIxs: repayParams.cpiIxs,
            cpiLuts: repayParams.cpiLuts,
            cpiSigners: repayParams.cpiSigners,
            // Hoisted ATA creates ride their own tx via unifySetup: false. Empty setupIxs → BE
            // no-ops and emits a single main tx as before.
            setupIxs: repayParams.setupIxs,
            setupLuts: repayParams.setupLuts,
            setupSigners: repayParams.setupSigners,
            unifySetup: false,
            mintToReceive: repayParams.mintToReceive,
            userPublicKey: activeWallet,
            loopName,
            simulate: true,
            analyticsInfo: createAnalyticsInfo<LoopAnalyticsInfo>({
                transactionType: AnalyticsTransactionType.LoopUnwind,
                principalMint: principalMetadata.mint,
                principalSymbol: principalMetadata.symbol,
                collateralSymbol: loanCollateral.metadata.symbol,
                loopIdentifier: loopName,
                collateralMint: loanCollateral.assetMint,
                mintToReceive: repayParams.mintToReceive,
                slippagePercentDecimals,
                inputAmount: externalQuote?.quote?.inputAmount,
                outputAmount: externalQuote?.quote?.minOutputAmount
            })
        };
    }, [
        loanCollateral,
        loanAddress,
        principalMetadata,
        activeWallet,
        debtToRepay,
        repayParams.cpiIxs,
        repayParams.cpiLuts,
        repayParams.cpiSigners,
        repayParams.mintToReceive,
        externalQuote,
        slippagePercentDecimals,
        withdrawCollateral,
        loopName
    ]);
}

function useRepayLoopPreviewBalances({
    loanExpanded,
    loanCollateral,
    debtToRepay,
    repayTxnParams,
    externalQuote,
    windType,
    repayTx,
    useJitoSimulation,
    swapInstructionSteps,
    swapCollateralParams,
    getTransactionsWithParamsAndSimulation
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loanCollateral: LoanCollateralExpanded | undefined;
    debtToRepay: number | undefined;
    externalQuote: SwapInstructionsResponse | undefined;
    windType: WindType;
    repayTxnParams: RepayLoanTxnParams | undefined;
    repayTx: ReturnType<typeof useRepayLoanTransaction>;
    useJitoSimulation: boolean;
    swapInstructionSteps: RouteInstructionStep[];
    swapCollateralParams: SwapCollateralTxnParams | undefined;
    getTransactionsWithParamsAndSimulation: (
        params: BuildLoanTransactionsRequest
    ) => Promise<Result<SimulatedOrderedTransactions>>;
}): UseRepayLoopPreviewBalancesReturn {
    const quotePreviewBalanceChanges = useMemo(
        () =>
            getQuotePreviewBalanceChanges({
                quote: externalQuote?.quote,
                windType,
                loanCollateral,
                loanExpanded,
                debtToRepay
            }),
        [debtToRepay, externalQuote?.quote, loanCollateral, loanExpanded, windType]
    );

    const {
        previewBalances: selectedPreviewBalances,
        isLoading,
        error,
        hasSimulated
    } = useSelectUnwindSimulation({
        repayTxnParams,
        repayTx,
        useJitoSimulation,
        swapInstructionSteps,
        swapCollateralParams,
        getTransactionsWithParamsAndSimulation,
        loanExpanded
    });

    const previewBalances = isLoading
        ? undefined
        : hasSimulated && !error && selectedPreviewBalances !== undefined
          ? selectedPreviewBalances
          : quotePreviewBalanceChanges;

    return {
        previewBalances: previewBalances ?? undefined,
        isLoading,
        error,
        hasSimulated
    };
}

function useSelectUnwindSimulation({
    repayTxnParams,
    repayTx,
    useJitoSimulation,
    swapInstructionSteps,
    swapCollateralParams,
    getTransactionsWithParamsAndSimulation,
    loanExpanded
}: {
    repayTxnParams: RepayLoanTxnParams | undefined;
    repayTx: ReturnType<typeof useRepayLoanTransaction>;
    useJitoSimulation: boolean;
    swapInstructionSteps: RouteInstructionStep[];
    swapCollateralParams: SwapCollateralTxnParams | undefined;
    getTransactionsWithParamsAndSimulation: (
        params: BuildLoanTransactionsRequest
    ) => Promise<Result<SimulatedOrderedTransactions>>;
    loanExpanded: AbfLoanExpanded | undefined;
}): UseRepayLoopPreviewBalancesReturn {
    const {
        previewBalances: jitoPreviewBalances,
        error: jitoError,
        isSimulating: jitoIsSimulating,
        hasSimulated: jitoHasSimulated
    } = useJitoUnwindSimulation({
        skip: !useJitoSimulation,
        loanExpanded,
        repayTxnParams,
        swapInstructionSteps,
        swapCollateralParams,
        getTransactionsWithParamsAndSimulation
    });

    const {
        data: nonJitoData,
        isLoading: nonJitoLoading,
        error: nonJitoError,
        hasSimulated: nonJitoHasSimulated
    } = useSimulatedBalanceChanges({
        skip: useJitoSimulation,
        repayTxnParams,
        repayTx
    });

    // No external `enabled`, just pick based on which sim mode we're in
    if (useJitoSimulation) {
        return {
            previewBalances: jitoPreviewBalances,
            isLoading: jitoIsSimulating,
            error: jitoError,
            hasSimulated: jitoHasSimulated
        };
    }

    // Non-Jito sim: use repayTx-based preview balances
    return {
        previewBalances: nonJitoData,
        isLoading: nonJitoLoading,
        error: nonJitoError,
        hasSimulated: nonJitoHasSimulated
    };
}

function useJitoUnwindSimulation({
    skip,
    loanExpanded,
    repayTxnParams,
    swapInstructionSteps,
    swapCollateralParams,
    getTransactionsWithParamsAndSimulation
}: {
    skip: boolean;
    loanExpanded: AbfLoanExpanded | undefined;
    repayTxnParams: RepayLoanTxnParams | undefined;
    swapInstructionSteps: RouteInstructionStep[];
    swapCollateralParams: SwapCollateralTxnParams | undefined;
    getTransactionsWithParamsAndSimulation: (
        params: BuildLoanTransactionsRequest
    ) => Promise<Result<SimulatedOrderedTransactions>>;
}): UseJitoUnwindSimulationReturn {
    const [previewBalances, setPreviewBalances] = useState<BalanceChanges | undefined>(undefined);
    const [error, setError] = useState<boolean>(false);
    const [isSimulating, setIsSimulating] = useState(false);
    const [hasSimulated, setHasSimulated] = useState(false);
    const simulateRef = useRef(getTransactionsWithParamsAndSimulation);
    const lastSimulationKeyRef = useRef<string | undefined>();

    useEffect(() => {
        simulateRef.current = getTransactionsWithParamsAndSimulation;
    }, [getTransactionsWithParamsAndSimulation]);

    const simulationKey = useMemo(() => {
        if (skip || !loanExpanded || !repayTxnParams) return undefined;
        const swapKey = swapCollateralParams
            ? JSON.stringify({
                  withdraw: swapCollateralParams.withdrawCollateral,
                  deposit: swapCollateralParams.depositCollateral
              })
            : undefined;
        return JSON.stringify({
            loan: loanExpanded.loan.address,
            repayAmount: repayTxnParams.repayParams?.[0]?.amount,
            mintToReceive: repayTxnParams.mintToReceive,
            swapSteps: swapInstructionSteps.length,
            swapKey
        });
    }, [skip, loanExpanded, repayTxnParams, swapInstructionSteps.length, swapCollateralParams]);

    useEffect(() => {
        let cancelled = false;

        async function simulate() {
            if (!simulationKey || !loanExpanded || !repayTxnParams) {
                lastSimulationKeyRef.current = undefined;
                setPreviewBalances(undefined);
                setError(false);
                setIsSimulating(false);
                setHasSimulated(false);
                return;
            }

            if (lastSimulationKeyRef.current === simulationKey) return;
            lastSimulationKeyRef.current = simulationKey;

            setError(false);
            setPreviewBalances(undefined);
            setIsSimulating(false);
            setHasSimulated(false);

            const steps: BuildLoanTransactionsRequest["steps"] = [];

            if (swapInstructionSteps.length > 1) {
                if (!swapCollateralParams) {
                    setError(true);
                    setHasSimulated(true);
                    return;
                }
                steps.push({ type: "swapCollateral", params: swapCollateralParams });
            }

            steps.push({ type: "repay", params: repayTxnParams });

            const buildRequest: BuildLoanTransactionsRequest = {
                loanAddress: loanExpanded.loan.address,
                steps
            };

            setIsSimulating(true);
            const simulateResult = await simulateRef.current(buildRequest);
            if (cancelled) return;
            setError(simulateResult.isErr());
            setPreviewBalances(simulateResult.isOk() ? simulateResult.unwrap().balanceChanges : undefined);
            setIsSimulating(false);
            setHasSimulated(true);
        }

        void simulate();

        return () => {
            cancelled = true;
        };
    }, [loanExpanded, repayTxnParams, simulationKey, swapCollateralParams, swapInstructionSteps]);

    return { previewBalances, error, isSimulating, hasSimulated };
}

function buildSimulatedCollateralForRepay({
    loanExpanded,
    swapCollateralParams,
    resolveMetadataForMint,
    jitoCloseEnabled
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    swapCollateralParams: SwapCollateralTxnParams | undefined;
    resolveMetadataForMint: (mint: string | undefined) => TokenListMetadata | undefined;
    jitoCloseEnabled: boolean;
}): LoanCollateral | undefined {
    if (!jitoCloseEnabled || !loanExpanded || !swapCollateralParams) return undefined;

    const baseCollaterals = loanExpanded.collateral.map((collateral) => ({
        loan: loanExpanded.loan.address,
        id: collateral.id,
        index: collateral.index,
        assetType: collateral.assetType,
        assetMint: collateral.assetMint,
        assetIdentifier: collateral.assetIdentifier,
        amount: uiAmountToLamports(collateral.amount, collateral.metadata.decimals ?? 0)
    }));

    const collateralsByIndex = new Map<number, LoanCollateral>();
    baseCollaterals.forEach((collateral) => collateralsByIndex.set(collateral.index, collateral));

    const recycledIndices: number[] = [];
    swapCollateralParams.withdrawCollateral.forEach(({ collateralMint, amount }) => {
        const existing = baseCollaterals.find((collateral) => collateral.assetMint === collateralMint);
        if (!existing) return;

        const remainingAmount = Math.max(existing.amount - amount, 0);
        collateralsByIndex.set(existing.index, { ...existing, amount: remainingAmount });
        recycledIndices.push(existing.index);
    });

    let nextIndex = baseCollaterals.reduce((maxIndex, collateral) => Math.max(maxIndex, collateral.index), -1) + 1;

    swapCollateralParams.depositCollateral.forEach((deposit) => {
        const mint = getMintFromAssetData(deposit.collateralAssetData);
        const metadata = resolveMetadataForMint(mint);
        if (!mint || !metadata) return;

        const assetIdentifier = getAssetIdentifier(metadata) ?? mint;
        const assetType = getAssetType(metadata);
        const index = recycledIndices.length ? (recycledIndices.shift() ?? nextIndex) : nextIndex++;

        collateralsByIndex.set(index, {
            loan: loanExpanded.loan.address,
            index,
            assetType,
            assetMint: mint,
            assetIdentifier,
            amount: deposit.collateralAmount
        });
    });

    return Array.from(collateralsByIndex.values()).filter((collateral) => collateral.amount > 0)[0];
}

function getQuotePreviewBalanceChanges({
    quote,
    windType,
    loanCollateral,
    loanExpanded,
    debtToRepay
}: {
    quote: SwapInstructionsResponse["quote"] | undefined;
    windType: WindType;
    loanCollateral: LoanCollateralExpanded | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    debtToRepay: number | undefined;
}): BalanceChanges | undefined {
    if (!quote || !loanCollateral || !loanExpanded || debtToRepay === undefined) return undefined;

    const totalCollateralLamports = uiAmountToLamports(loanCollateral.amount, loanCollateral.metadata.decimals);
    const swapInputLamports = quote.inputAmount ?? 0;
    const minOutputLamports = quote.minOutputAmount ?? 0;
    const debtLamports = uiAmountToLamports(debtToRepay, loanExpanded.principalMetadata.decimals);

    if (windType === WindType.Collateral) {
        const balanceChanges: BalanceChanges = {};

        const remainingCollateralLamports = Math.max(totalCollateralLamports - swapInputLamports, 0);
        if (remainingCollateralLamports > 0) {
            balanceChanges[loanCollateral.assetMint] = remainingCollateralLamports;
        }

        const leftoverPrincipalLamports = Math.max(minOutputLamports - debtLamports, 0);
        if (leftoverPrincipalLamports > 0) {
            balanceChanges[loanExpanded.principalMetadata.mint] = leftoverPrincipalLamports;
        }

        return Object.keys(balanceChanges).length ? balanceChanges : undefined;
    }

    if (windType === WindType.Principal) {
        const receivePrincipalLamports = Math.max(minOutputLamports - debtLamports, 0);
        if (receivePrincipalLamports > 0) {
            return {
                [loanExpanded.principalMetadata.mint]: receivePrincipalLamports
            };
        }
    }

    return undefined;
}

function ReceiveOptionsList({
    selectedOption,
    setSelectedOption,
    options,
    loading,
    resolveMetadataForMint,
    loopPosition
}: {
    selectedOption: UnwindOption;
    setSelectedOption: (option: UnwindOption) => void;
    options: Record<UnwindOption, UnwindOptionData> | undefined;
    loading: boolean;

    resolveMetadataForMint: (mint: string | undefined) => TokenListMetadata | undefined;
    loopPosition: LoopPositionExpanded | undefined;
}) {
    const allowedWindTypes = loopPosition?.allowedWindTypes ?? [];
    const optionKeys: UnwindOption[] = [];

    if (allowedWindTypes.includes(WindType.Collateral)) {
        optionKeys.push("collateral");
    }
    if (allowedWindTypes.includes(WindType.Principal)) {
        optionKeys.push("principal");
    }

    return (
        <OptionSelectList
            loading={loading}
            skeletonCount={optionKeys.length || 2}
            options={optionKeys}
            getKey={(o) => o}
            isSelected={(o) => selectedOption === o && !options?.[o]?.hasQuoteError && !options?.[o]?.simulationFailed}
            isDisabled={(o) =>
                !!options?.[o]?.hasQuoteError || !!options?.[o]?.isFetchingQuote || !!options?.[o]?.simulationFailed
            }
            onSelect={setSelectedOption}
            sx={{ maxHeight: "none", py: 1 }}
        >
            {(option) => {
                return (
                    <SpinnerOverlay loading={!!options?.[option]?.isFetchingQuote}>
                        <UnwindReceiveOptionButtonContent
                            simulationFailed={options?.[option]?.simulationFailed ?? false}
                            option={option}
                            loopPosition={loopPosition}
                            optionSummary={options?.[option]}
                            resolveMetadataForMint={resolveMetadataForMint}
                            isLoading={loading}
                        />
                    </SpinnerOverlay>
                );
            }}
        </OptionSelectList>
    );
}

type SpinnerOverlayProps = {
    loading: boolean;
    children: React.ReactNode;
};

function SpinnerOverlay({ loading, children }: SpinnerOverlayProps) {
    const theme = useTheme();
    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            <Box
                sx={{
                    opacity: loading ? 0.4 : 1,
                    pointerEvents: loading ? "none" : "auto",
                    transition: "opacity 0.2s"
                }}
            >
                {children}
            </Box>
            {loading && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none"
                    }}
                >
                    <CircularProgress size="1rem" sx={{ color: theme.palette.text.disabled }} />
                </Box>
            )}
        </Box>
    );
}

function UnwindReceiveOptionButtonContent({
    option,
    optionSummary,
    resolveMetadataForMint,
    loopPosition,
    isLoading,
    simulationFailed
}: {
    option: UnwindOption;
    optionSummary: UnwindOptionData | undefined;
    resolveMetadataForMint: (mint: string | undefined) => TokenListMetadata | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    isLoading: boolean;
    simulationFailed: boolean;
}) {
    const loanExpanded = loopPosition?.loanExpanded;
    const collateral = getZcCollateral(loanExpanded);
    const principalMint = loanExpanded?.principalMetadata?.mint;
    const collateralMint = collateral?.assetIdentifier;

    const hasQuoteError = optionSummary?.hasQuoteError;

    if (hasQuoteError) {
        const assetMint = option === "collateral" ? collateralMint : principalMint;
        const assetMeta = resolveMetadataForMint(assetMint);
        const assetLabel = assetMeta?.symbol ?? assetMint ?? "asset";

        return (
            <Column spacing={1} sx={{ width: "100%", minWidth: 0 }}>
                <Row spaceBetween sx={{ width: "100%" }}>
                    <Row spacing={0.5}>
                        <TokenImage size="md" metadata={assetMeta} />
                    </Row>
                </Row>
                <Row spacing={0.5}>
                    <Text variant="body2" color="caption" translate="no">
                        {`Unable to get unwind quote for ${assetLabel}`}
                    </Text>
                </Row>
            </Column>
        );
    }

    const { tokenStats } = optionSummary ?? {};
    const receiveAmounts = getReceiveDisplayList({
        option,
        tokenStats,
        collateralMint,
        principalMint,
        resolveMetadataForMint
    });
    const primary = receiveAmounts[0];
    const secondaries = receiveAmounts.slice(1);

    return (
        <Column spacing={0.5} sx={{ width: "100%", minWidth: 0 }}>
            {primary && (
                <Row spaceBetween sx={{ width: "100%" }}>
                    <Row spacing={0.5} sx={{ minWidth: 0 }}>
                        <TokenImage size="md" metadata={primary.token} />
                        <Text variant="h4" loading={isLoading} loadingWidth="80px" translate="no">
                            {BsMetaUtil.formatAmount(primary.token, primary.amount, {
                                shortSymbol: true
                            })}
                        </Text>
                    </Row>
                    <Text variant="body1" loadingWidth="60px" loading={isLoading} translate="no">
                        {formatUsd(tokenStats?.receiveUsdAmount ?? 0)}
                    </Text>
                </Row>
            )}

            {secondaries.length > 0 ? (
                <Column spacing={0.25}>
                    {secondaries.map((ra) => (
                        <Row key={ra.mint} spaceBetween sx={{ width: "100%" }}>
                            <Row spacing={0.5}>
                                <Text color="caption">+</Text>
                                <TokenImage size="xs" metadata={ra.token} />
                                <Text variant="body2" translate="no">
                                    {BsMetaUtil.formatAmount(ra.token, ra.amount, {
                                        shortSymbol: true
                                    })}
                                </Text>
                            </Row>
                            <Text color="caption" variant="body2" translate="no">
                                {formatUsd(ra.amountUsd)}
                            </Text>
                        </Row>
                    ))}
                </Column>
            ) : (
                <Row spacing={0.5}>
                    <Text color="caption">+</Text>
                    <Text color="caption" variant="body2" translate="no">
                        {formatUsd(0)}
                    </Text>
                </Row>
            )}
        </Column>
    );
}

type ReceiveAmountDisplay = {
    mint: string;
    token: TokenListMetadata | undefined;
    amount: number | undefined;
    amountUsd: number | undefined;
};

function getReceiveDisplayList({
    option,
    tokenStats,
    collateralMint,
    principalMint,
    resolveMetadataForMint
}: {
    option: UnwindOption;
    tokenStats: UnwindTokenStats | undefined;
    collateralMint: string | undefined;
    principalMint: string | undefined;
    resolveMetadataForMint: (mint: string | undefined) => TokenListMetadata | undefined;
}): ReceiveAmountDisplay[] {
    if (!tokenStats?.balanceChanges) return [];

    const balanceChanges = tokenStats.balanceChanges;
    const tokenPrices = tokenStats.tokenPrices ?? {};

    const preferredMint = option === "collateral" ? collateralMint : principalMint;
    const loopMints = new Set([collateralMint, principalMint].filter(Boolean));

    const orderedMints =
        preferredMint && balanceChanges[preferredMint] !== undefined
            ? [preferredMint, ...Object.keys(balanceChanges).filter((mint) => mint !== preferredMint)]
            : Object.keys(balanceChanges);

    return orderedMints
        .filter((mint) => {
            // Hide SOL rent refunds when SOL isn't part of the loop
            if (mint === WRAPPED_SOL_MINT && !loopMints.has(WRAPPED_SOL_MINT)) return false;
            return true;
        })
        .map((mint) => {
            const token = resolveMetadataForMint(mint);
            const amount = balanceChanges[mint];
            const price = tokenPrices[mint];
            const amountUsd = price !== undefined ? amount * price : undefined;
            return { mint, token, amount, amountUsd };
        });
}

function useDisplayController(loopPosition: LoopPositionExpanded | undefined) {
    const isMobile = useMediaQuery(MEDIA.SM.below);

    const defaultWindType = getDefaultWindType(loopPosition);
    const defaultOption: UnwindOption = defaultWindType === WindType.Principal ? "principal" : "collateral";
    const [selectedOption, setSelectedOption] = useState<UnwindOption>(defaultOption);
    const [displayType, setDisplayType] = useState<LoopDisplayType>(LoopDisplayType.UsdCostBasis);

    const allowedOptions = useMemo(() => {
        const windTypes = loopPosition?.allowedWindTypes ?? [];
        return {
            collateral: windTypes.includes(WindType.Collateral),
            principal: windTypes.includes(WindType.Principal)
        };
    }, [loopPosition?.allowedWindTypes]);

    // `defaultOption` only seeds the initial state, and on first render `loopPosition` may not have
    // arrived yet — which defaults us to "collateral" and, on a principal-only loop, would leave the
    // selection pointing at a route that doesn't exist and the CTA disabled for good. Fall back to
    // the default whenever the stored pick isn't allowed, so a valid deliberate choice still wins.
    const effectiveOption = allowedOptions[selectedOption] ? selectedOption : defaultOption;

    const setDisplayTypeHandler = () => {
        if (isMobile) return;
        setDisplayType(
            displayType === LoopDisplayType.TokenCostBasis
                ? LoopDisplayType.UsdCostBasis
                : LoopDisplayType.TokenCostBasis
        );
    };

    return {
        selectedOption: effectiveOption,
        setSelectedOption,
        displayType,
        setDisplayTypeHandler
    };
}

function SecondaryStats({
    loading,
    error,
    loopPosition,
    windType,
    tokenStats,
    slippagePercentDecimals,
    externalQuote,
    inNestedDialogView
}: {
    loading: boolean;
    error: boolean;
    loopPosition: LoopPositionExpanded | undefined;
    windType: WindType | undefined;
    tokenStats: UnwindTokenStats | undefined;
    slippagePercentDecimals: number | undefined;
    externalQuote: SwapInstructionsResponse | undefined;
    inNestedDialogView: boolean;
}) {
    const { principalUsdOraclePrice, collateralUsdOraclePrice } = useLoanOraclePriceQuotes(
        loopPosition?.loopExpanded?.principalToken,
        getZcCollateral(loopPosition?.loanExpanded)
    );

    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const debtAmount = ledger?.ledgerDebt.total;
    const collateral = getZcCollateral(loopPosition?.loanExpanded);
    const collateralAmount = collateral?.amount;

    const fairMarketValue = calculateLoopFairMarketValue({
        collateralAmount: collateralAmount,
        debtAmount: debtAmount,
        collateralOraclePrice: collateralUsdOraclePrice,
        principalOraclePrice: principalUsdOraclePrice
    });

    // this is the total impact of the swap, including fees, slippage, price impact, and oracle price deviation
    const executionDetails = useExecutionDetails({
        slippagePercentDecimals,
        externalQuote,
        receiveUsdAmount: tokenStats?.receiveUsdAmount,
        fairMarketValue,
        tokenBalanceChanges: tokenStats?.balanceChanges,
        labelOverrides: getLabelOverridesForUnwind(windType, loopPosition?.loopExpanded?.principalToken.symbol),
        principalUsdPrice: loopPosition?.loanExpanded?.principalUsdPrice,
        collateralUsdPrice: collateralUsdOraclePrice,
        principalMetadata: loopPosition?.loopExpanded?.principalToken,
        collateralMetadata: loopPosition?.loopExpanded?.collateralToken
    });

    return (
        <Column spacing={0.5}>
            {!error && (
                <LoanPositionStats
                    loanExpanded={loopPosition?.loanExpanded}
                    loopPosition={loopPosition}
                    newPrincipalAmount={0}
                    newCollateralAmount={0}
                    isFullRepay={true}
                    loading={loading}
                    executionDetails={executionDetails}
                    show={{
                        debtAndCollateral: true,
                        health: false,
                        netApyCollapsable: false,
                        executionDetails: true,
                        pnl: inNestedDialogView
                    }}
                />
            )}
        </Column>
    );
}

function getLabelOverridesForUnwind(
    windType: WindType | undefined,
    principalTokenSymbol?: string
): Partial<ExecutionDetailsLabels> {
    return {
        executionImpact: {
            caption: "Execution impact",
            tooltip:
                windType === WindType.Collateral
                    ? `Combined worst-case impact of slippage, fees, price impact, and market price on your expected return. Accounts for any additional ${principalTokenSymbol} received`
                    : `Combined worst-case impact of slippage, fees, price impact, and market price on your expected return.`
        },
        oracleSpread: {
            caption: "Oracle spread",
            tooltip:
                windType === WindType.Collateral
                    ? `Adjustment due to difference in oracle price and swap price. You may receive ${principalTokenSymbol} if there is a mismatch in the estimated and executed price.`
                    : `Adjustment due to difference in oracle price and swap price`
        }
    };
}
